<!-- Series switcher: one mini plate per series, newest first, current
     series centred. Outside the data block so it stays put while the next
     series loads. Draft: dev2 hosts and /new-release-draft only. -->
<nav class="series-crumbs" aria-label="Breadcrumb" *ngIf="isSeriesRoute">
  <div class="wrap">
    <a routerLink="/">Home</a><span aria-hidden="true">/</span>
    <a routerLink="/plates">Plates</a><span aria-hidden="true">/</span>
    <a routerLink="/plates/current-style">Current style</a><span aria-hidden="true">/</span>
    <span aria-current="page">{{ releaseYear }} Number Plates</span>
  </div>
</nav>

<pla-new-release-year-rail
  *ngIf="showYearRail"
  [year]="releaseYear"
  [basePath]="yearRailBase">
</pla-new-release-year-rail>

<ng-container *ngIf="data as d; else loadingTpl">
  <pla-new-release-hero
    [series]="d.series"
    [heading]="seriesHeading"
    [ticker]="d.ticker"
    (byoSearch)="onByoSearch($event)">
  </pla-new-release-hero>
  <section class="series-about" *ngIf="isSeriesRoute">
    <div class="wrap">
      <h2>About {{ releaseYear }} reg plates</h2>
      <p>
        {{ releaseYear }} number plates, also called {{ releaseYear }} reg plates or
        {{ releaseYear }} registration plates, belong to the {{ seriesPeriod }} registration series.
        The &lsquo;{{ releaseYear }}&rsquo; is the DVLA age identifier for new vehicles registered
        from {{ seriesDates }}.
      </p>
      <p>
        DVLA issues a new age identifier twice a year. The March identifier is the last two digits
        of the year and the September identifier adds 50, so {{ releaseYear }} pairs with the
        {{ sisterSeries }} series. A private {{ releaseYear }} registration can be put on a vehicle first
        registered on or after the start of its series, but never on an older vehicle, because a
        registration cannot make a vehicle look newer than it is.
      </p>
    </div>
  </section>

  <pla-new-release-resold mode="strip" [year]="d.series.yearDisplay"></pla-new-release-resold>

  <!-- Pre-release: countdown to DVLA's sale (27: 10:00, Wed 4 Nov 2026). -->
  <pla-new-release-countdown
    *ngIf="d.series.preRelease && d.series.saleOpensAt"
    [opensAt]="d.series.saleOpensAt"
    [year]="d.series.yearDisplay">
  </pla-new-release-countdown>

  <!-- Chapter navigation — the dev2 Register pattern (initials / names /
       number-ones). Sticky under the nav shell; only lists sections that
       actually render for this series. Replaces the old global chips row. -->
  <nav class="chapnav" aria-label="Sections" *ngIf="chapters.length">
    <div class="wrap">
      <a *ngFor="let c of chapters; trackBy: trackByChapter"
         href="#{{ c.id }}"
         (click)="jumpTo(c.id, $event)">{{ c.label }}</a>
    </div>
  </nav>

  <!-- Inline BYO results — data + chrome inherited from the legacy
       /build-your-own page. `byoResults` is a SearchReqResponse from
       `SearchService.searchBYO` (same backend `process=byo` call), passed
       straight into the canonical <pla-search-result-table>. That gives
       us the row layout (plate / favourite / plate-viewer / rank / price /
       View) + pagination + sort + skeleton-loading for free — identical
       to /build-your-own; site-wide BYO affordances unified. -->
  <section class="byo-results" id="byo-results" *ngIf="byoLoading || byoResults">
    <div class="wrap">
      <div class="byo-results-header">
        <span class="eyebrow">Build your own</span>
        <h2 class="byo-results-title" *ngIf="d.series.preRelease">{{ byoSearchTerm }}: price TBC</h2>
        <h2 class="byo-results-title" *ngIf="byoLoading && !d.series.preRelease">Searching for {{ byoSearchTerm }}&hellip;</h2>
        <h2 class="byo-results-title" *ngIf="!byoLoading && byoResults && !d.series.preRelease">
          {{ byoResults.registrations.length }}
          <ng-container *ngIf="byoResults.registrations.length === 1">plate matches</ng-container>
          <ng-container *ngIf="byoResults.registrations.length !== 1">plates match</ng-container>
          <span class="byo-results-term plate-font">{{ byoSearchTerm }}</span>
        </h2>
        <a class="byo-results-clear" role="button" tabindex="0"
           (click)="clearByoSearch()" (keydown.enter)="clearByoSearch()">
          &larr; Clear search
        </a>
      </div>

      <p class="byo-prerelease" *ngIf="d.series.preRelease && saleOpen">
        The {{ d.series.yearDisplay }} plates are on sale now at DVLA. Our search doesn&rsquo;t cover DVLA&rsquo;s live {{ d.series.yearDisplay }} stock yet:
        <a [href]="dvlaSearchUrl" target="_blank" rel="noopener">check <strong>{{ byoSearchTerm }}</strong> on DVLA</a>.
      </p>
      <p class="byo-prerelease" *ngIf="d.series.preRelease && !saleOpen">
        DVLA hasn&rsquo;t published the {{ d.series.yearDisplay }} stock yet, so there&rsquo;s nothing to search.
        <strong>{{ byoSearchTerm }}</strong> follows the {{ d.series.yearDisplay }} format. Its price is TBC until DVLA puts the series on sale
        ({{ d.series.releasedDate || 'expected November' }}).
      </p>

      <pla-search-result-table
        *ngIf="!d.series.preRelease && (byoResults || byoLoading)"
        [searching]="byoLoading"
        [searchTitle]="byoSearchTerm"
        [searchType]="''"
        [resultType]="'PLATE'"
        [pluralResultType]="'PLATES'"
        [showFilter]="false"
        [results]="byoResults"
        [resultChange]="byoResultChange"
        [paginationPageSize]="30">
      </pla-search-result-table>
    </div>
  </section>

  <main class="content">

      <!-- Pre-release notice: everything below is combinations that CAN be
           made. No prices exist until DVLA publishes the series. -->
      <section class="prerelease-note" *ngIf="d.series.preRelease"><div class="wrap">
        <span class="eyebrow">{{ saleOpen ? 'On sale now' : 'Before release' }}</span>
        <p *ngIf="d.series.dvlaChecked && saleOpen">
          DVLA&rsquo;s {{ d.series.yearDisplay }} sale opened at 10am on {{ d.series.releasedDate }}. Each plate below shows the price DVLA
          published, or whether DVLA is holding it back for auction or not offering it. Plates are selling now, so some may already have
          gone: open a plate to check it on DVLA.
        </p>
        <p *ngIf="d.series.dvlaChecked && !saleOpen">
          DVLA has published its {{ d.series.yearDisplay }} prices. The sale opens at 10am on {{ d.series.releasedDate }}, so nothing can be
          bought yet. Each plate below shows the price DVLA will sell it for, or whether DVLA is holding it back for auction or not
          offering it. Tap <strong>Notify me</strong> and we&rsquo;ll tell you when the sale opens.
        </p>
        <p *ngIf="!d.series.dvlaChecked">
          DVLA hasn&rsquo;t put the {{ d.series.yearDisplay }} series on sale yet. Every plate below is a valid {{ d.series.yearDisplay }}
          combination with its price <strong>TBC</strong>. DVLA sets prices on release day, and some of these plates will be held back
          for auction or withheld. Tap <strong>Notify me</strong> and we&rsquo;ll tell you when a plate becomes buyable.
        </p>
      </div></section>

      <!-- Honest empty state when the wordbank has no entries for this series.
           No fixture plates are ever rendered — see the page service comment
           for the real-data-only contract. -->
      <section class="section section--empty"
               *ngIf="d.perfectPlates.totalCount === 0 && d.names.totalCount === 0 && !obscured"><div class="wrap">
        <div class="sec-head">
          <span class="eyebrow">No listings yet</span>
          <h2>Nothing to show for the {{ d.series.yearDisplay }} series yet.</h2>
          <p>
            The wordbank has no perfect-plate or name entries for this series at the moment. Check back once the DVLA catalogue is loaded, or
            try the Build-Your-Own composer above. It searches every plate.
          </p>
        </div>
      </div></section>

      <!-- Signed in (or any series without the gate): the full page. -->
      <ng-container *ngIf="!obscured">
        <ng-container *ngTemplateOutlet="listsTpl"></ng-container>
        <ng-container *ngTemplateOutlet="openTpl"></ng-container>
      </ng-container>

      <!-- Signed out on a gated pre-release series (27): same order and
           chapter tabs as signed in, but the plate sections are obscured in
           place. Placeholder plates only; no real list entry is rendered.
           Likely held back and Banned stay open below. -->
      <ng-container *ngIf="obscured">
        <section class="section gate" [class.section--tint]="tint('perfect')" id="perfect" *ngIf="gatedLists.perfect"><div class="wrap">
          <div class="sec-head">
            <span class="eyebrow"><span class="ix">{{ ix('perfect') }}</span>Perfect plates</span>
            <h2>The best plates in the {{ d.series.yearDisplay }} series.</h2>
            <p *ngIf="d.series.digitReading">{{ d.series.digitReading }}</p>
          </div>

          <div class="gate-body">
            <ng-container *ngTemplateOutlet="teaserTpl"></ng-container>

            <div class="gate-card" role="region" aria-labelledby="gate-title" *ngIf="gated">
              <svg class="gate-lock" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <rect x="5" y="11" width="14" height="10" rx="2" fill="none" stroke="currentColor" stroke-width="1.8"/>
                <path d="M8 11V8a4 4 0 0 1 8 0v3" fill="none" stroke="currentColor" stroke-width="1.8"/>
              </svg>
              <h3 id="gate-title" class="gate-title">More to discover with PlateX.</h3>
              <p class="gate-lead">
                Create your free account to unlock the full {{ d.series.yearDisplay }} release research, plus tools to discover, compare and track private registrations.
              </p>

              <ul class="gate-perks">
                <li>
                  <strong>Save your favourite plates.</strong>
                  Keep a shortlist of registrations you like and return to them whenever you want.
                </li>
                <li>
                  <strong>Get alerts for new listings.</strong>
                  Save your searches and be notified when matching registrations appear for sale or at auction.
                </li>
                <li>
                  <strong>Compare dealer prices.</strong>
                  See where a registration is advertised and compare prices across dealers, no account needed.
                </li>
                <li>
                  <strong>Track your collection.</strong>
                  Add the plates you own to your private portfolio and follow their estimated value.
                </li>
                <li>
                  <strong>Access exclusive research.</strong>
                  Explore detailed plate lists, market analysis and research available to registered members<ng-container *ngIf="guidesOn">, including The Fixed-Price Guide</ng-container>.
                </li>
              </ul>

              <div class="gate-actions">
                <button type="button" class="gate-btn gate-btn--primary" (click)="openGateSignIn('register')">Join free</button>
                <button type="button" class="gate-btn" (click)="openGateSignIn('login')">Sign in</button>
              </div>
              <p class="gate-small">Registration is completely free and brings you straight back to this page.</p>
              <p class="gate-small" *ngIf="unlockLabel">The full {{ d.series.yearDisplay }} list opens to members at {{ unlockLabel }}, the evening before DVLA&rsquo;s sale.</p>
            </div>

            <div class="gate-card gate-card--member" role="region" aria-labelledby="gate-member-title" *ngIf="locked">
              <svg class="gate-lock" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2"/>
              </svg>
              <h3 id="gate-member-title" class="gate-title">You&rsquo;re in.</h3>
              <p class="gate-lead">
                Your full {{ d.series.yearDisplay }} list opens at {{ unlockLabel }}, the evening before DVLA&rsquo;s sale.
                Come back then to see every plate we expect at a fixed price.
              </p>
              <pla-new-release-countdown
                [opensAt]="d.series.listsUnlockAt || ''"
                [year]="d.series.yearDisplay"
                mode="list">
              </pla-new-release-countdown>
              <p class="gate-small">
                Get ready before the sale opens: set up your DVLA account and have your payment details to hand.
              </p>
            </div>
          </div>
        </div></section>

        <section class="section gate gate--names" [class.section--tint]="tint('names')" id="names" *ngIf="gatedLists.names"><div class="wrap">
          <div class="sec-head">
            <span class="eyebrow"><span class="ix">{{ ix('names') }}</span>{{ d.series.yearDisplay }} in names</span>
            <h2>{{ d.series.yearDisplay }} in names.</h2>
            <p>Five-letter names split by the year. Read the letters and skip the numbers.</p>
          </div>

          <div class="gate-body gate-body--slim">
            <ng-container *ngTemplateOutlet="teaserTpl"></ng-container>
            <div class="gate-inline">
              <svg class="gate-lock" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <rect x="5" y="11" width="14" height="10" rx="2" fill="none" stroke="currentColor" stroke-width="1.8"/>
                <path d="M8 11V8a4 4 0 0 1 8 0v3" fill="none" stroke="currentColor" stroke-width="1.8"/>
              </svg>
              <p *ngIf="gated">Every {{ d.series.yearDisplay }} name plate is in your free account.</p>
              <p *ngIf="locked">The names open with the rest of your list at {{ unlockLabel }}.</p>
              <div class="gate-actions" *ngIf="gated">
                <button type="button" class="gate-btn gate-btn--primary" (click)="openGateSignIn('register')">Join free</button>
                <button type="button" class="gate-btn" (click)="openGateSignIn('login')">Sign in</button>
              </div>
            </div>
          </div>
        </div></section>

        <ng-container *ngTemplateOutlet="openTpl"></ng-container>
      </ng-container>

      <!-- Blurred placeholder plates behind the gate. -->
      <ng-template #teaserTpl>
        <div class="gate-teaser" aria-hidden="true">
          <div class="grid">
            <div class="gate-teaser-card" *ngFor="let p of teaserPlates">
              <pla-plate [rawPlate]="p"
                         [showPrice]="false"
                         [openModal]="false"
                         [clickThrough]="false"
                         [noMargin]="true"></pla-plate>
              <span class="gate-teaser-line"></span>
              <span class="gate-teaser-line gate-teaser-line--short"></span>
            </div>
          </div>
        </div>
      </ng-template>

      <section class="section" [class.section--tint]="tint('qa')" id="qa" *ngIf="d.faqs?.length"><div class="wrap qa-wrap">
        <div class="sec-head">
          <span class="eyebrow"><span class="ix">{{ ix('qa') }}</span>Common questions</span>
          <h2>Questions about the {{ d.series.yearDisplay }} series.</h2>
        </div>
        <div class="faqs">
          <details class="faq-item" *ngFor="let faq of d.faqs">
            <summary>{{ faq.question }}</summary>
            <div class="faq-answer">{{ faq.answer }}</div>
          </details>
        </div>
      </div></section>

  </main>

  <!-- Fixed-price lists (perfect plates, names). Gated for signed-out
       readers on a pre-release series; see `gated`. -->
  <ng-template #listsTpl>
      <section class="section" [class.section--tint]="tint('perfect')" id="perfect" *ngIf="d.perfectPlates.totalCount > 0"><div class="wrap">
        <div class="sec-head">
          <span class="eyebrow"><span class="ix">{{ ix('perfect') }}</span>Perfect plates</span>
          <h2>The best plates in the {{ d.series.yearDisplay }} series.</h2>
          <!-- The 'what is a perfect plate' explainer lives on Standouts · Words; this block also holds
               Our picks, repeating letters and Almost perfect, so it only says how the year reads. -->
          <p *ngIf="d.series.digitReading">{{ d.series.digitReading }}</p>
        </div>

        <!-- Singular Editor's Pick block at the top of Perfect Plates.
             Permanent commercial claim about the release itself — strength
             of read, recognisability, why these specific plates stand out.
             Plate state (price / sold / auction) is carried by each card's
             own chrome; this block is editorial only. Naming specific
             plates here is fine because the claim is about plate quality,
             not availability. -->
        <div class="editors-pick" *ngIf="d.series.editorsPickCopy && !d.series.ourPicks?.length">
          <div class="editors-pick-eyebrow">Editor&rsquo;s pick &middot; the {{ d.series.yearDisplay }} release</div>
          <p class="editors-pick-copy">
            The standout reads of the {{ d.series.yearDisplay }} series:
            <ng-container *ngFor="let pick of d.series.editorsPicks; let last = last"><strong>{{ pick }}</strong><ng-container *ngIf="!last">, </ng-container></ng-container>.
            {{ d.series.editorsPickCopy }}
          </p>
        </div>

        <div class="subsection"
             *ngFor="let section of d.perfectPlates.sections; let i = index; trackBy: trackBySection">
          <div class="subsection-header">
            <div class="subsection-header-text">
              <h3 class="subsection-title">
                <span class="subsection-index">{{ (i + 1) | number:'2.0-0' }} &middot;</span>
                {{ section.title }}
              </h3>
              <p class="subsection-desc">{{ section.description }}</p>
            </div>
            <label class="subsection-sort">
              Sort:
              <select class="subsection-sort-select"
                      [value]="sortKeys[section.id] || 'quality_desc'"
                      (change)="setSort(section.id, $any($event.target).value)">
                <option *ngFor="let opt of sortOptions" [value]="opt.value">{{ opt.label }}</option>
              </select>
            </label>
          </div>

          <div class="grid">
            <pla-new-release-plate-card
              *ngFor="let card of visibleSectionCards(section); trackBy: trackByPlate"
              [card]="card"
              [tracked]="isTracked(card)"
              [favourited]="isFavourited(card)"
              (trackClicked)="onTrack($event)"
              (favouriteClicked)="onFavourite($event)">
            </pla-new-release-plate-card>
          </div>

          <div class="see-all" *ngIf="hasMore(section.id, section.cards.length); else allShownSection">
            <a role="button"
               tabindex="0"
               (click)="loadMoreSection(section)"
               (keydown.enter)="loadMoreSection(section)">Load {{ remainingInSection(section.id, section.cards.length) }} more &rarr;</a>
            <span class="see-all-count">Showing {{ sectionShowingCount(section.id) }} of {{ section.totalCount }}</span>
          </div>
          <ng-template #allShownSection>
            <div class="see-all see-all--exhausted" *ngIf="section.cards.length > 0">
              <span class="see-all-count">All {{ section.totalCount }} plates shown</span>
            </div>
          </ng-template>
        </div>
      </div></section>

      <pla-new-release-resold [year]="d.series.yearDisplay"></pla-new-release-resold>


      <section class="section" [class.section--tint]="tint('names')" id="names" *ngIf="d.names.totalCount > 0"><div class="wrap">
        <div class="sec-head">
          <span class="eyebrow"><span class="ix">{{ ix('names') }}</span>{{ d.series.yearDisplay }} in names</span>
          <h2>{{ d.series.yearDisplay }} in names.</h2>
          <p>Five-letter names split by the year. Read the letters and skip the numbers.</p>
        </div>

        <div class="subsection">
          <div class="subsection-header">
            <div class="subsection-header-text">
              <h3 class="subsection-title">
                <span class="subsection-index">{{ d.perfectPlates.sections.length + 1 | number:'2.0-0' }} &middot;</span>
                {{ d.series.yearDisplay }} in names.
              </h3>
              <p class="subsection-desc">Sorted by demand and commercial appeal. Use the A-Z filter to browse the full alphabet.</p>
            </div>
            <label class="subsection-sort">
              Sort:
              <select class="subsection-sort-select"
                      [value]="sortKeys['names'] || 'quality_desc'"
                      (change)="setSort('names', $any($event.target).value)">
                <option *ngFor="let opt of sortOptions" [value]="opt.value">{{ opt.label }}</option>
              </select>
            </label>
          </div>

          <!-- Per-subsection Editor's Pick featured-strip removed (brief:
               "Use only ONE Editor's Pick block near the top of Perfect
               Plates. Do NOT repeat in every subsection."). The names grid
               leads directly with the A-Z filter + card grid. -->

          <pla-new-release-az-pills
            [azCounts]="d.names.azCounts"
            [activeLetter]="selectedLetter"
            (letterClicked)="onAzLetter($event)">
          </pla-new-release-az-pills>

          <div class="names-filter-meta" *ngIf="selectedLetter">
            Showing names starting with <strong>{{ selectedLetter }}</strong>:
            {{ filteredNames().length }} match<span *ngIf="filteredNames().length !== 1">es</span>.
            <a role="button" tabindex="0" (click)="onAzLetter(null)" (keydown.enter)="onAzLetter(null)">Clear filter</a>
          </div>

          <div class="grid" id="names-grid">
            <pla-new-release-plate-card
              *ngFor="let card of visibleNameCards(); trackBy: trackByPlate"
              [card]="card"
              [tracked]="isTracked(card)"
              [favourited]="isFavourited(card)"
              (trackClicked)="onTrack($event)"
              (favouriteClicked)="onFavourite($event)">
            </pla-new-release-plate-card>
          </div>

          <div class="see-all" *ngIf="hasMore('names', filteredNames().length); else allShownNames">
            <a role="button"
               tabindex="0"
               (click)="loadMoreNames()"
               (keydown.enter)="loadMoreNames()">Load {{ filteredNames().length - (visibleCounts['names'] || 0) }} more &rarr;</a>
            <span class="see-all-count">Showing {{ sectionShowingCount('names') }} of {{ filteredNames().length }}</span>
          </div>
          <ng-template #allShownNames>
            <div class="see-all see-all--exhausted" *ngIf="filteredNames().length > 0">
              <span class="see-all-count">All {{ filteredNames().length }} plates shown</span>
            </div>
          </ng-template>
        </div>
      </div></section>

  </ng-template>

  <!-- Open half: auction, first sold (launch speed), banned. -->
  <ng-template #openTpl>
      <!-- Heading to auction — DVLA-held-back plates surfaced on their own
           dedicated surface, between Names and First-Sold. Featured/marquee
           slots upstream (Hero / Perfect Plates / Names) intentionally
           exclude these so the buy-page intent stays clean. Hidden when
           the series has no auction_reserved entries. -->
      <pla-new-release-heading-to-auction
        [data]="d.headingToAuction"
        [year]="d.series.yearDisplay"
        [ix]="ix('heading-to-auction')"
        [tint]="tint('heading-to-auction')"
        [isTracked]="isTrackedFn"
        [isFavourited]="isFavouritedFn"
        (trackClicked)="onTrack($event)"
        (favouriteClicked)="onFavourite($event)">
      </pla-new-release-heading-to-auction>

      <!-- First-sold launch order book — after the wordbank tabs (perfect +
           names), before banned. Pure data: plate / price / date stamp. -->
      <pla-new-release-first-sold
        [data]="d.firstSold"
        [year]="d.series.yearDisplay"
        [ix]="ix('first-sold')"
        [tint]="tint('first-sold')"
        [terms]="plateTerms">
      </pla-new-release-first-sold>

      <section class="section" [class.section--tint]="tint('banned')" id="banned"><div class="wrap">
        <div class="sec-head">
          <span class="eyebrow"><span class="ix">{{ ix('banned') }}</span>{{ d.banned.predicted ? 'Prediction' : 'Withheld by the DVLA' }}</span>
          <h2>{{ d.banned.predicted ? 'Likely banned in ' + d.series.yearDisplay + '.' : 'Banned in ' + d.series.yearDisplay + '.' }}</h2>
          <div class="banned-intro" *ngIf="d.banned.intro">
            <p>{{ d.banned.intro }}</p>
            <p class="banned-source" *ngIf="d.banned.source">
              <small>Source: {{ d.banned.source }}.</small>
            </p>
          </div>
        </div>

        <!-- Categorised groups (5 buckets from the trial layout: crude, slurs,
             drugs, death/violence, other for war/politics). Each renders as
             label + count + 9 sample pills + "+N more" via the
             pla-new-release-banned-group presentational component. -->
        <ng-container *ngIf="bannedExpanded && d.banned.groups?.length">
          <pla-new-release-banned-group
            *ngFor="let group of d.banned.groups"
            [group]="group">
          </pla-new-release-banned-group>
        </ng-container>

        <!-- Wildcard patterns row — shown alongside the grouped categories,
             never folded inside one since they apply across all categories
             (the asterisk can be any leading letter). Visually de-emphasised
             and non-navigable. -->
        <div class="banned-list-section" *ngIf="bannedExpanded && d.banned.wildcards?.length">
          <span class="banned-list-section-label" *ngIf="!d.banned.predicted">Wildcard patterns ({{ d.banned.wildcards!.length }}). The asterisk matches any leading letter.</span>
          <span class="banned-list-section-label" *ngIf="d.banned.predicted">Patterns we expect DVLA to block ({{ d.banned.wildcards!.length }}). An asterisk matches any letter: some come from how 27 reads, the rest carry over from earlier series&rsquo; wildcards</span>
          <div class="banned-list">
            <span class="banned-plate-wrap banned-plate-wrap--wildcard"
                  *ngFor="let pattern of d.banned.wildcards">
              <pla-plate [rawPlate]="pattern"
                         [showPrice]="false"
                         [customClass]="'flat shaded'"
                         [xSmallPlate]="true"
                         [openModal]="false"
                         [clickThrough]="false"
                         [noMargin]="true"></pla-plate>
            </span>
          </div>
        </div>

        <!-- Empty state — only when the data file hasn't been populated. -->
        <div class="banned-empty"
             *ngIf="!d.banned.groups?.length && !d.banned.plates?.length && !d.banned.wildcards?.length">
          The banned-plates list for the {{ d.series.yearDisplay }} series has not been loaded into this build yet.
        </div>

        <!-- Click-to-expand full banned list. Each grouped category only
             shows 9 sample pills + a "+N more" counter because the data
             doesn't carry per-category full lists; the FOI feed comes as
             one flat array of 513 specific combinations (BANNED_76_PLATES).
             This expand reveals that flat list inline below the categories,
             so every banned plate is reachable without leaving the page. -->
        <div class="see-all" *ngIf="(d.banned.totalCount > 0 || d.banned.groups?.length || d.banned.wildcards?.length) && !bannedExpanded" style="margin-top: 36px;">
          <a role="button" tabindex="0"
             (click)="bannedWarning = true"
             (keydown.enter)="bannedWarning = true">Show all {{ d.banned.totalCount }} {{ d.banned.predicted ? 'likely banned' : 'banned' }} plates &darr;</a>
        </div>
        <div class="banned-list-section" *ngIf="bannedExpanded && d.banned.plates?.length" style="margin-top: 24px;">
          <span class="banned-list-section-label">All specific combinations ({{ d.banned.plates!.length }})</span>
          <div class="banned-list">
            <span class="banned-plate-wrap"
                  *ngFor="let plate of d.banned.plates"
                  role="link" tabindex="0"
                  (click)="onBannedPlateClick(plate)"
                  (keydown.enter)="onBannedPlateClick(plate)">
              <pla-plate [rawPlate]="plate"
                         [showPrice]="false"
                         [customClass]="'flat shaded'"
                         [xSmallPlate]="true"
                         [openModal]="false"
                         [clickThrough]="false"
                         [noMargin]="true"></pla-plate>
            </span>
          </div>
          <div class="see-all see-all--exhausted" style="margin-top: 20px;">
            <span class="see-all-count">All {{ d.banned.totalCount }} {{ d.banned.predicted ? 'likely banned' : 'banned' }} plates shown</span>
          </div>
        </div>

        <!-- Pre-release only: the weaker tier (banned in exactly one of
             26 / 75 / 76). Shown separately so the headline count stays the
             high-confidence set. -->
        <div class="banned-list-section" *ngIf="bannedExpanded && d.banned.possiblePlates?.length" style="margin-top: 32px;">
          <span class="banned-list-section-label">Possible: banned in one earlier series ({{ d.banned.possiblePlates!.length }}). Historically a third to a half of these come back</span>
          <div class="banned-list">
            <span class="banned-plate-wrap"
                  *ngFor="let plate of d.banned.possiblePlates"
                  role="link"
                  tabindex="0"
                  (click)="onBannedPlateClick(plate)"
                  (keydown.enter)="onBannedPlateClick(plate)">
              <pla-plate [rawPlate]="plate"
                         [showPrice]="false"
                         [customClass]="'flat shaded'"
                         [xSmallPlate]="true"
                         [openModal]="false"
                         [clickThrough]="false"
                         [noMargin]="true"></pla-plate>
            </span>
          </div>
        </div>
      </div></section>

      <!-- Banned lists hold crude and abusive words: a warning before they open. -->
      <div class="banned-warning-backdrop" *ngIf="bannedWarning" (click)="bannedWarning = false">
        <div class="banned-warning" role="dialog" aria-modal="true" aria-labelledby="banned-warning-title"
             (click)="$event.stopPropagation()" (keydown.escape)="bannedWarning = false">
          <h3 id="banned-warning-title">Before you look</h3>
          <p>
            DVLA bans these combinations because they read as something crude, abusive or upsetting, so some of
            the plates in this list are offensive. We show them so you know what can&rsquo;t be bought, not to cause offence.
          </p>
          <div class="banned-warning-actions">
            <button type="button" class="banned-warning-cancel" (click)="bannedWarning = false">Cancel</button>
            <button type="button" class="banned-warning-ok"
                    (click)="bannedWarning = false; bannedExpanded = true">Show the list</button>
          </div>
        </div>
      </div>

  </ng-template>
</ng-container>

<ng-template #loadingTpl>
  <div *ngIf="isLoading" class="page-loading" aria-live="polite">
    Loading {{ releaseYear }} series&hellip;
  </div>
  <div *ngIf="isUnavailable" class="page-unavailable" aria-live="polite">
    <h1>{{ releaseYear }} series</h1>
    <p>
      We&rsquo;re still preparing the data for the {{ releaseYear }} series.
      The new-release page will populate once the endpoint and phrase-quality
      backfill are in place.
    </p>
    <p class="page-unavailable-meta">
      Looking for the previous design? It&rsquo;s available at
      <a [routerLink]="['/new-release-draft', releaseYear]">/new-release-draft/{{ releaseYear }}</a>.
    </p>
  </div>
</ng-template>
